import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, AuthState } from '../types';

const API_URL = '/api';
// Factory workers go long stretches with hands on materials (cutting fabric,
// assembling housing) and not touching the tablet. 30 min was too short —
// kicking them mid-task. 12h covers a full shift; the natural session break
// is when they explicitly hit "Logout" or a new worker logs in.
const IDLE_TIMEOUT = 12 * 60 * 60 * 1000;
const IDLE_WARNING = (12 * 60 - 5) * 60 * 1000; // 5 min before kick

interface AuthContextType extends AuthState {
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      const savedToken = localStorage.getItem('auth_token');
      if (!savedToken) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/auth/profile`, {
          headers: { Authorization: `Bearer ${savedToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data.user || data);
          setToken(savedToken);
        } else {
          localStorage.removeItem('auth_token');
          setToken(null);
        }
      } catch {
        localStorage.removeItem('auth_token');
        setToken(null);
      }
      setLoading(false);
    };

    validateToken();
  }, []);

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await res.json();
    const user = data.user;

    // Validate portal access - only employees with workstation access
    if (user.user_type !== 'employee') {
      throw new Error('This account does not have workstation access');
    }

    // Check allowed_portals array
    if (!user.allowed_portals?.includes('workstation')) {
      throw new Error('This account does not have workstation access');
    }

    const newToken = data.accessToken;
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(user);
  };

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  }, []);

  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const warningTimer = useRef<ReturnType<typeof setTimeout>>();
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  useEffect(() => {
    if (!user) return;
    const resetIdle = () => {
      setShowIdleWarning(false);
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
      warningTimer.current = setTimeout(() => setShowIdleWarning(true), IDLE_WARNING);
      idleTimer.current = setTimeout(() => { logout(); window.location.href = '/login'; }, IDLE_TIMEOUT);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => { events.forEach(e => window.removeEventListener(e, resetIdle)); clearTimeout(idleTimer.current); clearTimeout(warningTimer.current); };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated: !!token && !!user, loading, login, logout }}>
      {children}
      {showIdleWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>&#9201;</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Session Expiring</h3>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>You'll be signed out in 5 minutes due to inactivity.</p>
            <button onClick={() => setShowIdleWarning(false)} style={{ padding: '8px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Stay Signed In</button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export default AuthContext;
